import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/features/auth/api/auth.api'
import { isTwoFactorChallenge, type AuthTokenResponse } from '@/features/auth/types'
import { resetEcho } from '@/core/realtime/echo'
import type { User } from '@/shared/types'
import {
  AUTH_SYNC_STORAGE_KEYS,
  clearAuthTokens,
  clearLogoutPending,
  getAccessToken,
  isLogoutPending,
  isRememberLogin,
  markLogoutPending,
  setAuthTokens,
  setRememberedLogin,
  shouldRefreshAccessToken,
} from '@/core/auth/storage'
import { bareHttp } from '@/core/api/http'
import { AUTH_ENDPOINTS } from '@/features/auth/api/auth.endpoints'
import { useNavigationStore } from '@/shared/stores/navigation.store'
import { safeRedirectPath } from '@/shared/utils/safe-redirect-path'
import {
  ensureValidAccessToken,
  isAuthRefreshLockHeld,
  isHardRefreshFailure,
  refreshSessionForced,
  startAuthSessionKeeper,
  stopAuthSessionKeeper,
} from '@/core/auth/session-refresh'

const WEB_PUSH_TOKEN_KEY = 'hr_ast_web_push_token'

async function syncWebPushIfConfigured(options: { requestPermission?: boolean } = {}): Promise<string | null> {
  try {
    const { isWebPushConfigured, syncWebPushRegistration, getWebPushBlockReason } = await import('@/core/push/web-push')
    if (!isWebPushConfigured()) {
      if (import.meta.env.DEV) console.warn('[web-push] not configured')
      return null
    }
    const token = await syncWebPushRegistration({ requestPermission: options.requestPermission === true })
    if (!token && import.meta.env.DEV) {
      const blockReason = getWebPushBlockReason()
      if (blockReason === 'permission_denied') return null
      const perm = typeof Notification !== 'undefined' ? Notification.permission : 'n/a'
      console.warn('[web-push] register skipped/failed', blockReason ?? perm)
    }
    return token
  } catch (error) {
    if (import.meta.env.DEV) console.warn('[web-push] sync error', error)
    return null
  }
}

async function clearWebPushIfStored(accessToken?: string | null): Promise<void> {
  if (!localStorage.getItem(WEB_PUSH_TOKEN_KEY)) return
  try {
    const { clearWebPushRegistration } = await import('@/core/push/web-push')
    await clearWebPushRegistration(accessToken)
  } catch {
    localStorage.removeItem(WEB_PUSH_TOKEN_KEY)
  }
}

function maybeSyncWebPushForUser(user: User | null | undefined): void {
  if (!user) return
  // Silent: only registers when Notification.permission is already granted.
  void syncWebPushIfConfigured()
}

/** Call from login/OTP submit (after priming permission on the same click). */
async function registerWebPushAfterLogin(): Promise<string | null> {
  return syncWebPushIfConfigured({ requestPermission: true })
}

let storageSyncInitialized = false
let fetchMeInFlight: Promise<void> | null = null
let ensureSessionInFlight: Promise<boolean> | null = null
/** Avoid refetch loops when /me still returns empty permissions for a broken role assign. */
let permissionsHydrationAttempted = false

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(getAccessToken())
  const user  = ref<User | null>(null)

  const isAuthenticated = computed(() => !!token.value)

  const canManageUsers = computed(() => {
    const u = user.value
    if (!u) return false
    return u.permissions?.includes('users.manage') ?? false
  })
  const dataScopeType = computed(() => user.value?.data_scope_type ?? 'self')
  const canBrowseAssignees = computed(() => user.value?.can_browse_assignees === true)
  const canReviewSupplierSubmissions = computed(
    () => user.value?.supplier_submission_review_available === true || can('supplier.submission.review'),
  )
  const canViewSupplierSubmissions = computed(
    () =>
      canReviewSupplierSubmissions.value
      || user.value?.supplier_submission_view_available === true
      || can('menu.supplier_submissions.view')
      || can('supplier.intake.view'),
  )

  function clearSession(options: { skipPushUnregister?: boolean } = {}): void {
    const accessToken = getAccessToken()
    fetchMeInFlight = null
    ensureSessionInFlight = null
    permissionsHydrationAttempted = false
    stopAuthSessionKeeper()
    if (options.skipPushUnregister) {
      localStorage.removeItem(WEB_PUSH_TOKEN_KEY)
    } else {
      void clearWebPushIfStored(accessToken)
    }
    resetEcho()
    clearAuthTokens()
    token.value = null
    user.value = null
    localStorage.removeItem('hr_ast_active_tenant_id')
    localStorage.removeItem('hr_ast_active_workspace_id')
    localStorage.removeItem('hr_ast_can_switch_tenant')
    localStorage.removeItem('hr_ast_can_switch_tenant')
    useNavigationStore().invalidate()
    void import('@/shared/composables/use-system-role-labels').then(({ resetSystemRoleLabels }) => {
      resetSystemRoleLabels()
    })
    void import('@/features/profile/profile-cache').then(({ resetProfileCache }) => {
      resetProfileCache()
    })
  }

  function redirectToLoginIfNeeded(): void {
    const path = window.location.pathname
    if (
      path.startsWith('/recruitment-portal')
      || path.startsWith('/r/')
      || path.startsWith('/login')
    ) {
      return
    }
    window.location.replace('/login')
  }

  function applyAuthSession(res: AuthTokenResponse, remember: boolean) {
    setAuthTokens(
      {
        access_token: res.access_token,
        expires_in: res.expires_in,
      },
      remember,
    )
    token.value = res.access_token
  }

  async function syncFromStorage(): Promise<void> {
    if (isLogoutPending()) {
      return
    }

    const stored = getAccessToken()

    if (stored === token.value) {
      if (stored && user.value && !shouldRefreshAccessToken()) {
        return
      }
      if (stored && shouldRefreshAccessToken()) {
        try {
          await ensureValidAccessToken()
        } catch (error) {
          if (isHardRefreshFailure(error)) {
            clearSession()
            redirectToLoginIfNeeded()
          }
        }
      }
      return
    }

    if (!stored) {
      // Peer tab may be mid-refresh (overwrite-in-place); don't treat as logout.
      if (isAuthRefreshLockHeld()) {
        return
      }
      if (token.value || user.value) {
        clearSession()
        redirectToLoginIfNeeded()
      }
      return
    }

    token.value = stored
    try {
      await ensureValidAccessToken()
      token.value = getAccessToken()
    } catch (error) {
      if (isHardRefreshFailure(error)) {
        clearSession()
        redirectToLoginIfNeeded()
        return
      }
      // Transient — keep stored token; fetchMe / next API call will retry.
    }

    await fetchMe()

    if (!token.value || !user.value) {
      redirectToLoginIfNeeded()
      return
    }

    startAuthSessionKeeper()
  }

  function initStorageSync(): void {
    if (storageSyncInitialized || typeof window === 'undefined') return
    storageSyncInitialized = true

    const runSync = () => {
      void syncFromStorage()
    }

    window.addEventListener('storage', (event) => {
      if (
        event.key === null
        || (AUTH_SYNC_STORAGE_KEYS as readonly string[]).includes(event.key)
      ) {
        runSync()
      }
    })

    window.addEventListener('auth:session-cleared', () => {
      clearSession()
    })
  }

  async function login(login: string, password: string, remember = true) {
    const res = await authApi.login(login, password, remember)
    if (isTwoFactorChallenge(res)) {
      return res
    }
    applyAuthSession(res, remember)
    useNavigationStore().invalidate()
    setRememberedLogin(login, remember)
    clearLogoutPending()
    user.value = res.user
    startAuthSessionKeeper()
    return res
  }

  async function verifyTwoFactor(challengeId: string, code: string, remember = true) {
    const res = await authApi.verifyTwoFactor(challengeId, code)
    applyAuthSession(res, remember)
    useNavigationStore().invalidate()
    clearLogoutPending()
    user.value = res.user
    startAuthSessionKeeper()
    return res
  }

  async function fetchMe(): Promise<void> {
    if (!token.value) return
    if (fetchMeInFlight) return fetchMeInFlight

    fetchMeInFlight = (async () => {
      try {
        await ensureValidAccessToken()
        const res = await authApi.me()
        user.value = res.user
        maybeSyncWebPushForUser(res.user)
        void useNavigationStore().fetchNavigation(true)
      } catch {
        try {
          await refreshSessionForced()
          const res = await authApi.me()
          user.value = res.user
          maybeSyncWebPushForUser(res.user)
          void useNavigationStore().fetchNavigation(true)
        } catch {
          clearSession()
        }
      } finally {
        fetchMeInFlight = null
      }
    })()

    return fetchMeInFlight
  }

  async function tryRestoreFromRefreshCookie(): Promise<boolean> {
    if (isLogoutPending()) {
      return false
    }

    try {
      const res = await authApi.refresh()
      applyAuthSession(res, isRememberLogin())
      clearLogoutPending()
      await fetchMe()
      return !!(token.value && user.value)
    } catch {
      return false
    }
  }

  function needsPermissionHydration(u: User | null): boolean {
    if (!u) return true
    if (permissionsHydrationAttempted) return false
    // Login used to serialize empty permissions when Spatie team was unset — refresh once.
    if (!Array.isArray(u.permissions)) return true
    if (u.permissions.length === 0 && (u.roles?.length ?? 0) > 0) return true
    return false
  }

  async function ensureSession(options: { allowCookieRefresh?: boolean } = {}): Promise<boolean> {
    const { allowCookieRefresh = true } = options
    if (isLogoutPending()) {
      return false
    }

    if (ensureSessionInFlight) return ensureSessionInFlight

    ensureSessionInFlight = (async () => {
      if (token.value && user.value && !needsPermissionHydration(user.value)) {
        startAuthSessionKeeper()
        return true
      }

      await syncFromStorage()

      if (token.value && user.value && !needsPermissionHydration(user.value)) {
        startAuthSessionKeeper()
        return true
      }

      if (token.value) {
        permissionsHydrationAttempted = true
        await fetchMe()
        if (user.value) {
          startAuthSessionKeeper()
          return true
        }
      }

      if (allowCookieRefresh && (await tryRestoreFromRefreshCookie())) {
        startAuthSessionKeeper()
        return true
      }

      clearSession()
      return false
    })().finally(() => {
      ensureSessionInFlight = null
    })

    return ensureSessionInFlight
  }

  /** Guest routes (login): redirect only when access token is still valid — no refresh. */
  async function redirectIfAuthenticated(
    redirect?: string,
  ): Promise<{ name: string } | { path: string } | null> {
    if (isLogoutPending()) {
      return null
    }

    await syncFromStorage()

    const postAuth = async (): Promise<{ name: string } | { path: string }> => {
      const safeRedirect = safeRedirectPath(redirect)
      if (safeRedirect) {
        return { path: safeRedirect }
      }
      return postLoginRoute()
    }

    if (token.value && user.value) {
      startAuthSessionKeeper()
      return postAuth()
    }

    if (token.value) {
      await fetchMe()
      if (user.value) {
        startAuthSessionKeeper()
        return postAuth()
      }
      clearSession()
    }

    return null
  }

  async function restoreSession(): Promise<boolean> {
    return ensureSession()
  }

  async function logout() {
    markLogoutPending()
    stopAuthSessionKeeper()
    resetEcho()

    const accessToken = getAccessToken()
    // Push unregister is best-effort — never block logout.
    void clearWebPushIfStored(accessToken)
    try {
      // Always hit logout so HttpOnly refresh cookie is cleared server-side,
      // even when the access token is already gone.
      await bareHttp.post(
        AUTH_ENDPOINTS.LOGOUT,
        {},
        accessToken
          ? { headers: { Authorization: `Bearer ${accessToken}` } }
          : undefined,
      )
    } catch {
      // Cookie/session may already be invalid — still clear locally
    } finally {
      clearSession({ skipPushUnregister: true })
    }
  }

  function can(permission: string): boolean {
    if (!user.value) return false
    return user.value.permissions?.includes(permission) ?? false
  }

  /** Resolve the landing page from the personalized navigation payload. */
  async function postLoginRoute(): Promise<{ name: string }> {
    const navigation = useNavigationStore()
    try {
      await navigation.fetchNavigation()
    } catch {
      // Login already issued tokens — do not surface a menu failure as "login failed".
    }

    return { name: navigation.firstAccessibleRouteName() ?? 'profile' }
  }

  return {
    token, user,
    isAuthenticated,
    canManageUsers,
    dataScopeType,
    canBrowseAssignees,
    canReviewSupplierSubmissions,
    canViewSupplierSubmissions,
    clearSession,
    syncFromStorage,
    initStorageSync,
    login,
    verifyTwoFactor,
    registerWebPushAfterLogin,
    fetchMe,
    ensureSession,
    redirectIfAuthenticated,
    restoreSession,
    logout,
    can,
    postLoginRoute,
  }
})
